/**
 * Language detection for headlines.
 *
 * The Mayor's Desk has to read a report in whatever language it arrived in
 * and hand the mayor a headline he can act on. That needs two things this
 * module provides:
 *
 *  1. the SCRIPT of the text — Thai, Arabic, CJK or Latin. Script detection
 *     is exact, because the Unicode ranges do not overlap.
 *  2. the LANGUAGE, for Latin script — Malay or English. This one is a
 *     marker-word heuristic and is documented as such: it decides whether a
 *     headline needs translating and what to label it, and it never invents
 *     content. A headline it cannot classify comes back "unknown" and is
 *     translated anyway.
 *
 * Malay matters here: Yala is a Malay-Muslim majority province, the
 * Deep South's other language is Patani Malay (Yawi), and the neighbouring
 * Malaysian press covers the border in Malay. A mayor who reads only Thai
 * and English would otherwise miss the other side of the border entirely.
 */

export type HeadlineLang = "th" | "ms" | "en" | "ar" | "zh" | "unknown";

export const HEADLINE_LANG_LABEL: Record<HeadlineLang, string> = {
  th: "ไทย",
  ms: "Melayu",
  en: "EN",
  ar: "العربية",
  zh: "中文",
  unknown: "?",
};

/** Marker words that mark a Latin-script headline as Malay. */
const MALAY_MARKERS = [
  "yang", "dan", "untuk", "dari", "pada", "akan", "telah", "adalah", "dalam",
  "tidak", "lebih", "satu", "orang", "tetapi", "juga", "karena", "oleh",
  "seperti", "kemudian", "malaysia", "polis", "berita", "pemerintah", "negara",
  "bandar", "dalam", "meningkat", "menurun", "satu", "ke", "di", "itu",
];

/** Marker words that mark a Latin-script headline as English. */
const ENGLISH_MARKERS = [
  "the", "and", "for", "with", "from", "that", "this", "has", "was", "are",
  "will", "says", "said", "after", "over", "into", "police", "government",
  "more", "than", "about", "after", "year", "killed", "arrested", "meeting",
];

/** The writing system a string is written in. */
export function detectScript(text: string): "thai" | "arabic" | "cjk" | "latin" | "unknown" {
  for (const ch of text) {
    const c = ch.codePointAt(0)!;
    if (c >= 0x0e00 && c <= 0x0e7f) return "thai";       // Thai
    if (c >= 0x0600 && c <= 0x06ff) return "arabic";     // Arabic
    if (c >= 0x0750 && c <= 0x077f) return "arabic";     // Arabic supplement
    if ((c >= 0x4e00 && c <= 0x9fff) || (c >= 0x3040 && c <= 0x30ff)) return "cjk";
    if ((c >= 0x0041 && c <= 0x005a) || (c >= 0x0061 && c <= 0x007a)) return "latin";
  }
  return "unknown";
}

/**
 * The language of a headline.
 *
 * Thai, Arabic and CJK are exact (script-determined). Latin script is
 * resolved by marker words: Malay markers versus English markers, and the
 * winner is the language. A text with neither is "unknown" — which the
 * brief treats as "translate it anyway".
 */
export function detectLang(text: string): HeadlineLang {
  const script = detectScript(text);
  if (script === "thai") return "th";
  if (script === "arabic") return "ar";
  if (script === "cjk") return "zh";
  if (script !== "latin") return "unknown";

  const words = text
    .toLowerCase()
    .replace(/[^\p{L}\p{M}\s]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);

  let ms = 0;
  let en = 0;
  for (const w of words) {
    if (MALAY_MARKERS.includes(w)) ms++;
    else if (ENGLISH_MARKERS.includes(w)) en++;
  }
  if (ms > en) return "ms";
  if (en > ms) return "en";
  return "unknown";
}

/** Does this headline need a translation to be readable? */
export function needsTranslation(text: string, readerLang: "en" | "th" | "zh"): boolean {
  const lang = detectLang(text);
  if (lang === "unknown") return true;
  return lang !== readerLang;
}

/** A short human label for a headline's source language. */
export function langLabel(lang: HeadlineLang): string {
  return HEADLINE_LANG_LABEL[lang] ?? "?";
}
